import jQuery from 'jquery';

const $ = jQuery;

/**
 * Core object managing all interactions for the directory search interface.
 * Caches DOM elements, handles department filtering and updates results.
 *
 * @namespace DirectorySearch
 */
const DirectorySearch = {
	/**
	* Initializes the search module by caching DOM nodes and binding event
	* listeners.
	*
	* @since 1.0.0
	* @returns {void}
	*/
	init() {
		this.cacheDOMElements();
		// If the container doesn't exist, stop execution
		if (!this.container) return;
		
		this.addEventListeners();
		this.initializeDepartmentSelection();
		
		// Trigger initial search if `q` param exists
		const initialQuery = UIUtils.getParameterByName('q');
		if (initialQuery) {
			this.searchInput.value = initialQuery;
			this.handleSearchChange();
			window.history.replaceState({}, '', window.location.pathname);
		}
	},
	
	/**
	* Caches frequently accessed DOM elements to avoid redundant queries.
	*
	* @since 1.0.0
	* @returns {void}
	*/
	cacheDOMElements() {
		const container = document.querySelector('#directorySearchContainer');
		if (!container) return;
	
		this.container = container;
		this.searchInput = container.querySelector('#directorySearchInput');
		this.searchTypeRadios = container.querySelectorAll('input[name="dirSearchType"][type="radio"]');
		this.deptSelect = container.querySelector('#directoryDepartmentSelect');
		this.results = container.querySelector('#directorySearchResults');
		this.resultsFeed = container.querySelector('#directorySearchResultsFeed');
	},
	
	/**
	* Adds event listeners for search input, radio buttons, and department
	* selection.
	*
	* @since 1.0.0
	* @returns {void}
	*/
	addEventListeners() {
		// 1) Debounced search input
		const debouncedSearch = UIUtils.debounce(
			() => this.handleSearchChange(),
			250
		);
		this.searchInput.addEventListener('input', debouncedSearch);
		
		// 2) Prevent the form from submitting on ENTER
		this.searchInput.addEventListener('keydown', e => {
			if (e.key === 'Enter') {
				e.preventDefault();
			}
		});
		
		// 3) Search type radio buttons
		this.searchTypeRadios.forEach(radio => {
			radio.addEventListener('change', () => {
				if (this.searchInput.value.trim() !== '') {
					this.handleSearchChange();
				}
			});
		});
		
		// 4) Department dropdown (native change)
		this.deptSelect.addEventListener('change', this.handleDeptChange.bind(this));
		
		// 5) Select2 hooks
		if (window.jQuery && $.fn.select2) {
			const bound = this.handleDeptChange.bind(this);
			// select2:select  -> when a value is chosen
			// select2:clear   -> when the clear (×) button is clicked
			// select2:unselect-> (multi-select) when an option is removed
			$(this.deptSelect).on('select2:select select2:clear select2:unselect', bound);
		}
	},
	
	/**
	* Handles initialization of department selection.
	* If the results feed element is missing, the function exits early.
	* When a department ID is already present in the feed, it will be
	* preselected; otherwise the department list is fetched from the API.
	*
	* @since 1.0.0
	* @returns {void}
	*/
	initializeDepartmentSelection() {
		if (!this.resultsFeed) return;
		
		const departmentsData = this.resultsFeed.getAttribute('data-departments');
	
		if (!departmentsData) {
			this.loadDepartmentsFromAPI();
		} else {
			this.setPreselectedDepartment(departmentsData);
		}
	},
	
	/**
	* Fetches department data from the directory API and populates the
	* department dropdown. If a department ID is found in the current
	* URL, that department is automatically selected and the related
	* results and information are loaded.
	*
	* @since 1.0.0
	* @returns {void}
	*/
	loadDepartmentsFromAPI() {
		DeptDataUtils.fetchAllDirectoryApiDepartmentData()
			.then(data => {
				DeptUIUtils.populateDepartmentDropdown(data, this.deptSelect);
	
				const deptFromUrl = UIUtils.getParameterByName('d');
				if (deptFromUrl) {					
					UIUtils.setSelectValue(this.deptSelect, deptFromUrl);
					this.loadDeptAndResults(deptFromUrl, data);
				}
			})
			.catch(error => console.error('Fetch error:', error));
	},
	
	/**
	* Applies a preselected department ID and hides the department
	* dropdown. This is used when a directory search page is scoped to
	* a single department.
	*
	* @since 1.0.0
	* @param {string} departmentId - The department identifier to select.
	* @returns {void}
	*/
	setPreselectedDepartment(departmentId) {
		this.deptSelect.innerHTML = `<option value="${departmentId}">${departmentId}</option>`;
		this.deptSelect.parentNode.classList.add('hidden');
	
		ResultsUtils.loadResults(this.resultsFeed, departmentId, this.searchInput.value, UIUtils.getSelectedRadio(this.searchTypeRadios));
	},
	
	/**
	* Handles changes in the search input and updates the results feed.
	*
	* @since 1.0.0
	* @returns {void}
	*/
	handleSearchChange() {
		ResultsUtils.loadResults(
			this.resultsFeed,
			this.deptSelect.value,
			this.searchInput.value.trim(),
			UIUtils.getSelectedRadio(this.searchTypeRadios)
		);
	},
	
	/**
	* Handles department selection changes and updates the browser history.
	*
	* @since 1.0.0
	* @returns {void}
	*/
	handleDeptChange() {
		const newDept = this.deptSelect.value;
		const curParams = UIUtils.getParameterByName(null, true); // Get full URLSearchParams object
		
		if (curParams.get('d') !== newDept) {
			curParams.set('d', newDept);
			window.history.pushState({}, '', `${window.location.pathname}?${curParams.toString()}`);
		}
		
		this.loadDeptAndResults(newDept);
	},
	
	/**
	 * Loads directory results and department information for a given
	 * department identifier.
	 *
	 * @since 1.0.0
	 * @param {string} deptId - Department identifier from the dropdown or URL.
	 * @param {Object[]} [data] - Optional department dataset to use.
	 * @returns {void}
	 */
	loadDeptAndResults(deptId, data) {
		const searchTerm = this.searchInput.value;
		const searchType = UIUtils.getSelectedRadio(this.searchTypeRadios);
		
		if (deptId) {
			ResultsUtils.loadResults(this.resultsFeed, deptId, searchTerm, searchType);
			ResultsUtils.loadDeptInfo(deptId, data);
		} else {
			// No department selected—reset dept info and results
			ResultsUtils.loadDeptInfo(null);
			ResultsUtils.loadResults(this.resultsFeed, '', searchTerm, searchType);
		}
	}
};

/**
 * Collection of helper methods for fetching and displaying directory results
 * and related department information.
 *
 * @namespace ResultsUtils
 */
const ResultsUtils = {
	/**
	* Loads department information for the given department identifier.
	*
	* @since 1.0.0
	* @param {string|null} deptId - Department identifier to load data for.
	* @param {Object[]} [data] - Optional preloaded department dataset.
	* @returns {void}
	*/
	loadDeptInfo(deptId, data) {
		const deptInfoLoader = document.querySelector('#deptInfoLoader');
		const deptInfoContent = document.querySelector('#deptInfoContent');
		
		if (!deptInfoLoader || !deptInfoContent) return;
		
		// Show the loader and hide the content
		LoaderManager.showLoader(deptInfoLoader);
		UIUtils.fadeToggle(deptInfoContent, false);
		
		const deptInfoName = deptInfoContent.querySelector('#deptInfoName');
		const deptInfoPhone = deptInfoContent.querySelector('#deptInfoPhone');
		const deptInfoWeb = deptInfoContent.querySelector('#deptInfoWeb');
		
		deptInfoPhone.classList.add('hidden');
		deptInfoWeb.classList.add('hidden');
	
		if (deptId) {
			let deptMatch = data ? DeptDataUtils.getDepartmentById(data, deptId) : null;
	
			if (deptMatch) {
				this.populateDeptInfo(deptMatch, deptInfoContent, deptInfoName, deptInfoPhone, deptInfoWeb, deptInfoLoader);
			} else {
				DeptDataUtils.fetchDirectoryApiDepartmentById(deptId)
					.then(department => this.populateDeptInfo(department, deptInfoContent, deptInfoName, deptInfoPhone, deptInfoWeb, deptInfoLoader))
					.catch(error => {
						console.error('Error fetching department:', error);
						LoaderManager.markLoaderComplete(deptInfoLoader);
						LoaderManager.waitForAllLoaders(() => { // Ensures loader is hidden even on error
							UIUtils.fadeToggle(deptInfoContent, false);
						});
					});
			}
		} else {
			// If no department ID is provided, hide the container
			LoaderManager.markLoaderComplete(deptInfoLoader);
			LoaderManager.waitForAllLoaders(() => { 
				UIUtils.fadeToggle(deptInfoContent, false);
			});
		}
	},
	
	/**
	* Populates the department information block with retrieved data.
	* Ensures the info only displays after the URL has loaded.
	*
	* @since 1.0.0
	* @param {Object|null} deptMatch - Department data to display.
	* @param {HTMLElement} deptInfoContent - Container for the information.
	* @param {HTMLElement} deptInfoName - Element where the name is printed.
	* @param {HTMLElement} deptInfoPhone - Phone link container.
	* @param {HTMLElement} deptInfoWeb - Website link container.
	* @param {HTMLElement} deptInfoLoader - Associated loader element.
	* @returns {void}
	*/
	async populateDeptInfo(deptMatch, deptInfoContent, deptInfoName, deptInfoPhone, deptInfoWeb, deptInfoLoader) {
		if (!deptMatch) {
			LoaderManager.markLoaderComplete(deptInfoLoader);
			LoaderManager.waitForAllLoaders(() => {
				UIUtils.fadeToggle(deptInfoContent, false);
			});
			return;
		}
		
		deptInfoName.textContent = deptMatch.Name;
		
		if (deptMatch.Phone) {
			deptInfoPhone.querySelector('.link').innerHTML =
				`<a href="tel:+1336278${deptMatch.Phone}">(336) 278-${deptMatch.Phone}</a>`;
			deptInfoPhone.classList.remove('hidden');
		}
		
		const deptUrlDataSource = deptInfoContent.getAttribute('data-dept-url-source');
		let deptUrl = deptMatch.Url || null;
		
		if (deptUrlDataSource === 'wordpress_api' || deptUrlDataSource === 'static_json') {
			try {
				const wpData = await DeptDataUtils.fetchWpDepartmentData(deptMatch.DeptId, deptUrlDataSource);
				deptUrl = wpData.redirect_url || wpData.alt_url || wpData.website_url || null;
			} catch (error) {
				console.error(`Error fetching WordPress (${deptUrlDataSource}) department data:`, error);
			}
		}
		
		// Ensure deptInfo only shows once everything is loaded
		this.displayDeptUrl(deptUrl, deptInfoWeb);
		
		// Mark loader as complete, then rely on waitForAllLoaders() to hide it and show content
		LoaderManager.markLoaderComplete(deptInfoLoader);
		LoaderManager.waitForAllLoaders(() => {
			UIUtils.fadeToggle(deptInfoContent, true);
		});
	},
	
	/**
	* Displays the department website URL within the provided container.
	*
	* @since 1.0.0
	* @param {string|null} deptUrl - Raw department website URL.
	* @param {HTMLElement} linkContainer - Element that will contain the link.
	* @returns {void}
	*/
	displayDeptUrl(deptUrl, linkContainer) {
		if (!deptUrl || !linkContainer) return;
	
		// Ensure the URL starts with https://
		let secureUrl = '';
		
		if (deptUrl.startsWith('http://')) {
			secureUrl = deptUrl.replace(/^http:\/\//, 'https://');
		} else if (deptUrl.startsWith('//')) {
			secureUrl = 'https:' + deptUrl;
		} else if (!deptUrl.startsWith('https://')) {
			secureUrl = 'https://' + deptUrl;
		} else {
			secureUrl = deptUrl;
		}
	
		// Strip protocol for display text (keep only 'www' and beyond)
		const displayUrl = secureUrl.replace(/^https?:\/\//, '');
	
		const linkElement = linkContainer.querySelector('.link');
		if (linkElement) {
			linkElement.innerHTML = `<a href="${secureUrl}">${displayUrl}</a>`;
			linkContainer.classList.remove('hidden');
		}
	},
	
	/**
	* Loads the search results into the directory feed element.
	*
	* @since 1.0.0
	* @param {HTMLElement} feedElement - Container that will hold the results.
	* @param {string} deptId - Selected department ID, may be empty.
	* @param {string} searchTerms - Raw search text from the user.
	* @param {string} searchType - Search mode (e.g., "name" or "department").
	* @returns {void}
	*/
	loadResults(feedElement, deptId, searchTerms, searchType) {
		if (!feedElement) {
			console.error('Results directory feed element not found.');
			return;
		}
		
		const resultsLoader = document.querySelector('#directorySearchResultsLoader');
		
		// Show the loader and hide the feed while loading
		LoaderManager.showLoader(resultsLoader);
		UIUtils.fadeToggle(feedElement, false);
	
		searchTerms = searchTerms.trim();
		
		this.clearFeed(feedElement);
		this.setFeedAttributes(feedElement, deptId, searchTerms, searchType);
		
		// Set status message to "Searching..." if inputs are not both empty
		UIUtils.setDirectoryStatusMessage(
			feedElement,
			searchTerms,
			DirectorySearch.deptSelect,
			!(searchTerms === '' && deptId === '')
		);
		
		// If search terms are empty and no department is selected, hide resultsFeed
		if (!searchTerms && !deptId) {
			UIUtils.fadeToggle(feedElement, false);
			LoaderManager.markLoaderComplete(resultsLoader);
			LoaderManager.waitForAllLoaders(() => {
				UIUtils.fadeToggle(feedElement, false);
			});
			return;
		}
		
		if (searchTerms.length > 1 || deptId !== '') {
			this.initializeDirectoryFeed(feedElement, resultsLoader);
		}
	},
	
	/**
	* Clears all previous results from the feed element.
	*
	* @since 1.0.0
	* @param {HTMLElement} feedElement - Element whose contents are removed.
	* @returns {void}
	*/
	clearFeed(feedElement) {
		feedElement.innerHTML = '';
	},
	
	/**
	* Applies department and search attributes used by the directory feed.
	*
	* @since 1.0.0
	* @param {HTMLElement} feedElement - Directory feed container.
	* @param {string} deptId - Selected department identifier.
	* @param {string} searchTerms - Sanitized search text.
	* @param {string} searchType - Selected search mode.
	* @returns {void}
	*/
	setFeedAttributes(feedElement, deptId, searchTerms, searchType) {
		// Handle department filtering
		if (deptId) {
			deptId = DeptDataUtils.checkCombined(deptId);
			feedElement.setAttribute('data-departments', deptId);
		} else {
			feedElement.removeAttribute('data-departments');
		}
		
		// Handle search filtering
		if (searchTerms) {
			feedElement.setAttribute('data-searchtype', searchType);
			feedElement.setAttribute('data-q', searchTerms);
		} else {
			feedElement.removeAttribute('data-searchtype');
			feedElement.removeAttribute('data-q');
		}
	},
	
	/**
	* Initializes the jQuery Directory plugin and waits for images to load.
	*
	* @since 1.0.0
	* @param {HTMLElement} feedElement - Directory feed container.
	* @param {HTMLElement} loader - Loader element shown while fetching.
	* @returns {void}
	*/
	initializeDirectoryFeed(feedElement, loader) {
		if (!feedElement) return;
		
		if (typeof $ !== 'undefined' && $.fn.Directory) {
			$(feedElement).Directory();
			
			// Ensure images are loaded before showing the feed
			this.waitForImagesAndStability(feedElement, loader);
		} else {
			console.warn('jQuery or Directory method not available.');
			this.showFeed(feedElement, loader);
		}
	},
	
	/**
	* Waits for all images inside the feed to load and then verifies layout
	* stability before revealing the results.
	*
	* @since 1.0.0
	* @param {HTMLElement} feedElement - Directory feed container.
	* @param {HTMLElement} loader - Loader element controlling visibility.
	* @returns {void}
	*/
	waitForImagesAndStability(feedElement, loader) {
		const images = feedElement.querySelectorAll('img');
	
		if (images.length === 0) {
			this.waitForStability(feedElement, loader);
			return;
		}
	
		let loadedImages = 0;
		images.forEach(img => {
			if (img.complete) {
				loadedImages++;
			} else {
				img.addEventListener('load', () => {
					loadedImages++;
					if (loadedImages === images.length) {
						this.waitForStability(feedElement, loader);
					}
				});
				img.addEventListener('error', () => {
					loadedImages++;
					if (loadedImages === images.length) {
						this.waitForStability(feedElement, loader);
					}
				});
			}
		});
	},
	
	/**
	* Ensures no further layout changes are occurring before revealing the
	* feed.
	*
	* @since 1.0.0
	* @param {HTMLElement} feedElement - Directory feed container.
	* @param {HTMLElement} loader - Loader element associated with the feed.
	* @returns {void}
	*/
	waitForStability(feedElement, loader) {
		let lastHeight = feedElement.scrollHeight;
		let stabilityTimer;
		let fallbackTimer;
	
		// Observe changes to detect when content stops shifting
		const observer = new MutationObserver(() => {
			clearTimeout(stabilityTimer);
			stabilityTimer = setTimeout(() => {
				let newHeight = feedElement.scrollHeight;
				if (newHeight === lastHeight) {
					clearTimeout(fallbackTimer);
					observer.disconnect();
					this.showFeed(feedElement, loader);
				} else {
					lastHeight = newHeight; // Reset the timer if height changes again
				}
			}, 500); // Wait 500ms after the last detected change
		});
	
		observer.observe(feedElement, { childList: true, subtree: true });
	
		// Fallback: If no changes are detected within 3 seconds, show the feed
		fallbackTimer = setTimeout(() => {
				observer.disconnect();
				this.showFeed(feedElement, loader);
		}, 3000);
	},
	
	/**
	* Ensures the feed is shown and the loader is hidden.
	*
	* @since 1.0.0
	* @param {HTMLElement} feedElement - Feed element to reveal.
	* @param {HTMLElement} loader - Loader element to mark complete.
	* @returns {void}
	*/
	showFeed(feedElement, loader) {
		LoaderManager.markLoaderComplete(loader);
		LoaderManager.waitForAllLoaders(() => {
			UIUtils.fadeToggle(feedElement, true);
			UIUtils.setDirectoryStatusMessage(feedElement, DirectorySearch.searchInput?.value?.trim() || '', DirectorySearch.deptSelect, false);
		});
	}
};

/**
 * Utility functions for retrieving and normalizing department data used by
 * the directory search interface.
 *
 * @namespace DeptDataUtils
 */
const DeptDataUtils = {
	/**
	* Fetches all department data from the directory API.
	*
	* @since 1.0.0
	* @returns {Promise<Object[]>} Promise resolving with department data.
	*/
	async fetchAllDirectoryApiDepartmentData() {
		try {
			const response = await fetch('/Directories/getDepartments.ashx');
			if (!response.ok) throw new Error(`HTTP error! Status: ${response.status}`);
			return await response.json();
		} catch (error) {
			console.error('Error fetching department data:', error);
			throw error;
		}
	},

	/**
	* Retrieves a specific department by ID from a given dataset.
	*
	* @since 1.0.0
	* @param {Object[]} departments - Collection of department objects.
	* @param {string} deptId - Identifier to search for.
	* @returns {Object|null} Matching department or `null` if not found.
	*/
	getDepartmentById(departments, deptId) {
		return departments.find(dept => dept.DeptId == deptId) || null;
	},

	/**
	* Fetches a single department by ID using the directory API.
	*
	* @since 1.0.0
	* @param {string} deptId - Department identifier to fetch.
	* @returns {Promise<Object|null>} Promise resolving with the department or `null`.
	*/
	async fetchDirectoryApiDepartmentById(deptId) {
		try {
			const departments = await this.fetchAllDirectoryApiDepartmentData();
			return this.getDepartmentById(departments, deptId);
		} catch (error) {
			console.error('Error fetching specific department data:', error);
			throw error;
		}
	},

	/**
	* Fetches department data from WordPress API or a static JSON file.
	*
	* @since 1.0.0
	* @param {string} deptId - Department identifier to fetch.
	* @param {string} [source='wordpress_api'] - Data source to use.
	* @returns {Promise<Object>} Promise resolving with WordPress department data.
	*/
	async fetchWpDepartmentData(deptId, source = 'wordpress_api') {
		const cb = new Date().getTime();
		const urlBase = source === 'wordpress_api' 
			? '/u/wp-json/api/v1/departments/it'
			: '/u/wp-content/json/departments/api-static';
		const url = source === 'wordpress_api'
			? `${urlBase}/${deptId}?v=${cb}`
			: `${urlBase}/${deptId}.json?v=${cb}`;

		try {
			const response = await fetch(url);
			if (!response.ok) throw new Error(`HTTP error! Status: ${response.status}`);
			return await response.json();
		} catch (error) {
			console.error('Error fetching WP department data:', error);
			throw error;
		}
	},

	/**
	* Adds public-friendly department names to the dataset.
	*
	* @since 1.0.0
	* @param {Object[]} data - Department data to augment.
	* @returns {void}
	*/
	addFriendlyNames(data) {
		if (!Array.isArray(data)) return;

		if (typeof elon_dir_vars === 'undefined' || !Array.isArray(elon_dir_vars.friendly_names)) {
			return;
		}

		for (const item of elon_dir_vars.friendly_names) {
			data.push({ DeptId: item.dir_app_id, Name: item.name });
		}
	},

	/**
	* Filters out removed departments from the dataset.
	* (either unnecessary or getting a more public-friendly name)
	*
	* @since 1.0.0
	* @param {Object[]} data - Department data list.
	* @returns {Object[]} Filtered department list.
	*/
	filterRemoved(data) {
		if (!Array.isArray(data)) return data;

		if (typeof elon_dir_vars === 'undefined' || !Array.isArray(elon_dir_vars.removed_depts)) {
			return data;
		}

		return data.filter(item => !elon_dir_vars.removed_depts.includes(item.DeptId));
	},

	/**
	* Checks for combined departments (some IDs represent multiple departments).
	* Some departments are really combinations of departments, so a single
	* DeptId can turn into a comma-separated list of departments.
	*
	* @since 1.0.0
	* @param {string} deptId - Potentially combined department identifier.
	* @returns {string} Adjusted identifier or original value.
	*/
	checkCombined(deptId) {
		if (typeof deptId !== 'string') return deptId;

		deptId = deptId.replace(/%2c/gi, ','); // Decode URL-encoded commas

		if (typeof elon_dir_vars === 'undefined' || !Array.isArray(elon_dir_vars.combined_depts)) {
			return deptId;
		}

		for (const group of elon_dir_vars.combined_depts) {
			if (!Array.isArray(group.if_ids) || !group.return_ids) continue;

			if (group.if_ids.includes(deptId)) {
				return group.return_ids;
			}
		}

		return deptId;
	},

	/**
	* Removes duplicate departments based on unique `DeptId|Name` keys.
	* This ensures that friendly name duplicates are preserved.
	*
	* @since 1.0.0
	* @param {Array} array - Array of department objects or strings.
	* @returns {Array} Deduplicated array.
	*/
	dedupe(array) {
		const dedupeObj = {};
		let itemKey;

		for (let i = 0; i < array.length; i++) {
			if (typeof array[i] === 'object') {
				itemKey = array[i].DeptId + '|' + array[i].Name;
				dedupeObj[itemKey] = array[i];
			} else {
				dedupeObj[array[i]] = true;
			}
		}

		array = [];

		for (const key in dedupeObj) {
			if (typeof dedupeObj[key] === 'object') {
				array.push(dedupeObj[key]);
			} else {
				array.push(key);
			}
		}

		return array;
	},

	/**
	* Sorts departments alphabetically by name.
	*
	* @since 1.0.0
	* @param {Object} a - First department object.
	* @param {Object} b - Second department object.
	* @returns {number} Comparison result for Array.prototype.sort.
	*/
	sortByName(a, b) {
		if (a.Name < b.Name) {
			return -1;
		} else if (a.Name > b.Name) {
			return 1;
		}
		return 0;
	},

	/**
	* Processes department data by applying deduplication, filtering and
	* sorting.
	*
	* @since 1.0.0
	* @param {Object[]} data - Raw department data from the API.
	* @returns {Object[]} Cleaned and sorted department data.
	*/
	processDepartments(data) {
		if (!data || !Array.isArray(data)) return [];

		data = this.dedupe(data);
		data = this.filterRemoved(data);
		this.addFriendlyNames(data);

		return data.sort(this.sortByName);
	}
};

/**
 * UI helper functions for populating and enhancing the department dropdown.
 *
 * @namespace DeptUIUtils
 */
const DeptUIUtils = {
	/**
	* Populates the department dropdown with a processed data set.
	*
	* @since 1.0.0
	* @param {Object[]} data - Raw department data.
	* @param {HTMLSelectElement} deptSelect - Dropdown to populate.
	* @returns {void}
	*/
	populateDepartmentDropdown(data, deptSelect) {
		const processedData = DeptDataUtils.processDepartments(data);
		const fragment = document.createDocumentFragment();

		const defaultOption = document.createElement('option');
		defaultOption.value = '';
		defaultOption.textContent = (typeof $ !== 'undefined' && $.fn && $.fn.select2) ? '' : 'Filter by Department';
		fragment.appendChild(defaultOption);

		processedData.forEach(dept => {
			const option = document.createElement('option');
			option.value = dept.DeptId;
			option.textContent = dept.Name.replace(/&amp;/g, '&');
			fragment.appendChild(option);
		});

		deptSelect.appendChild(fragment);
		
		// Initialize Select2 if available
		this.initializeSelect2(deptSelect);
	},
	
	/**
	* Initializes Select2 on the department dropdown if available.
	*
	* @since 1.0.0
	* @param {HTMLSelectElement} deptSelect - Dropdown element.
	* @returns {void}
	*/
	initializeSelect2(deptSelect) {
		if (typeof $ !== 'undefined' && $.fn.select2) {
			$(deptSelect).select2({
				placeholder: 'Filter by Department',
				allowClear: true
			});
		}
	},
};

/**
 * Global controller for skeleton loaders used throughout the directory search.
 * Manages loader visibility and accessibility state during asynchronous calls.
 *
 * @namespace LoaderManager
 */
const LoaderManager = {
	activeLoaders: new Set(),
	
	/**
	* Displays a loader element and sets accessibility attributes on its container.
	*
	* @since 1.0.0
	* @param {HTMLElement} loaderElement - Loader to show.
	* @returns {void}
	*/
	showLoader(loaderElement) {
		if (!loaderElement) return;
		this.activeLoaders.add(loaderElement);
		
		// Get the parent container
		const parentContainer = loaderElement.closest('.skeleton-loader-container');
		if (parentContainer) {
			// Set a dynamic min-height
			parentContainer.style.minHeight = `${loaderElement.offsetHeight}px`;
			// Accessibility enhancement
			parentContainer.setAttribute('aria-busy', 'true');
		}
		
		// Ensure classes don't overlap
		loaderElement.classList.remove('loaded', 'loading');
		loaderElement.classList.add('loading');
	},
	
	/**
	* Marks a loader as finished and removes ARIA attributes.
	*
	* @since 1.0.0
	* @param {HTMLElement} loaderElement - Loader to hide.
	* @returns {void}
	*/
	markLoaderComplete(loaderElement) {  // Previously "hideLoader()"
		if (!loaderElement) return;
		this.activeLoaders.delete(loaderElement);
		
		const parentContainer = loaderElement.closest('.skeleton-loader-container');
		if (parentContainer) {
			parentContainer.setAttribute('aria-busy', 'false');
		}
	},

	/**
	* Waits for all active loaders to complete before executing a callback.
	* Ensures that loaders are hidden **immediately before** content is shown.
	*
	* @since 1.0.0
	* @param {Function} callback - Function to run once loading completes.
	* @returns {void}
	*/
	waitForAllLoaders(callback) {
		if (this.activeLoaders.size === 0) {
			// Hide all loaders first, ensuring mutual exclusivity
			document.querySelectorAll('.loading, .loaded').forEach(loader => {
				loader.classList.remove('loading', 'loaded');
				loader.classList.add('loaded');
			});

			// Then execute the callback to show content
			callback();
			
			// Now reset min-height AFTER content is visible
			setTimeout(() => {
				document.querySelectorAll('.skeleton-loader-container').forEach(container => {
					container.style.minHeight = '';
				});
			}, 100); // Small delay ensures smooth transition
		} else {
			// Poll every 50ms to check if all loaders are done
			setTimeout(() => this.waitForAllLoaders(callback), 50);
		}
	}
};

/**
 * Global UI utility functions for the directory search interface.
 *
 * @namespace UIUtils
 */
const UIUtils = {
	/**
	 * Returns a debounced version of the supplied function.
	 *
	 * @since 1.0.0
	 * @param {Function} fn   Function to debounce.
	 * @param {number}   wait Delay in ms.
	 * @returns {Function} Debounced wrapper.
	 */
	debounce(fn, wait = 300) {
		let t;
		return function (...args) {
			const ctx = this;
			clearTimeout(t);
			t = setTimeout(() => fn.apply(ctx, args), wait);
		};
	},
	
	/**
	 * Toggles an element's visibility with a fade transition. Elements are faded
	 * in or out and the `.hidden` class is applied or removed at the end of the
	 * animation to ensure they do not occupy space when hidden.
	 *
	 * @since 1.0.0
	 * @param {HTMLElement} el - Element to show or hide.
	 * @param {boolean} show - If `true`, fades in; otherwise fades out.
	 * @param {number} [delay=150] - Delay before starting the fade-in (ignored for fade-out).
	 * @returns {void}
	 */
	fadeToggle(el, show, delay = 150) {
		if (!el) return;
		
		const isHidden = el.classList.contains('hidden');
		
		// Avoid duplicate animations if the element is already in the desired state
		if (show && !isHidden) return;
		if (!show && isHidden) return;
		
		if (show) {
			// Reveal the element immediately so the layout doesn't shift
			el.classList.remove('hidden');
			el.style.opacity = 0;
			
			const startFadeIn = () => {
				el.style.transition = 'opacity 0.3s';
				requestAnimationFrame(() => {
					el.style.opacity = 1;
				});
			};
			
			if (delay) {
				setTimeout(startFadeIn, delay);
			} else {
				startFadeIn();
			}
		   
			el.addEventListener(
				'transitionend',
				function handler() {
					el.style.transition = '';
					el.style.opacity = '';
					el.removeEventListener('transitionend', handler);
				},
				{ once: true }
			);
		} else {
			// Fade out quickly with no delay
			el.style.transition = 'opacity 0.01s';
			requestAnimationFrame(() => {
				el.style.opacity = 0;
			});
			   
			el.addEventListener(
				'transitionend',
				function handler() {
					el.classList.add('hidden');
					el.style.transition = '';
					el.style.opacity = '';
					el.removeEventListener('transitionend', handler);
				},
				{ once: true }
			);
		}
	},
	
	/**
	 * Sets the value of a select element, including Select2 support.
	 *
	 * Updates the select element's value to match the provided value. If the element
	 * is enhanced with Select2, this updates the visible UI and triggers the
	 * `change.select2` event to sync the display without firing native or custom
	 * `change` listeners unless specifically bound via jQuery.
	 *
	 * @since 1.0.0
	 * @param {HTMLSelectElement} selectEl - The select element to update.
	 * @param {string} [value=''] - The new value to set.
	 * @returns {void}
	 */
	setSelectValue(selectEl, value = '') {
		if (window.jQuery && $.fn.select2 && $(selectEl).data('select2')) {
			$(selectEl).val(value).trigger('change.select2'); // Update Select2 UI without logic trigger
		} else {
			selectEl.value = value;
		}
	},
	
	/**
	* Retrieves a query parameter from the current URL.
	*
	* @since 1.0.0
	* @param {string|null} name - The name of the parameter to fetch.
	*                             If `null` and `fullParams` is `true`,
	*                             the full `URLSearchParams` object is
	*                             returned instead.
	* @param {boolean} [fullParams=false] - Whether to return the entire
	*                                       `URLSearchParams` object.
	* @returns {string|URLSearchParams} The parameter value or the
	*                                   `URLSearchParams` instance.
	*/
	getParameterByName(name, fullParams = false) {
		const params = new URLSearchParams(window.location.search);
		return fullParams ? params : params.get(name) || '';
	},
	
	/**
	* Retrieves the value of the currently selected radio input.
	*
	* @since 1.0.0
	* @param {NodeList} radios - Collection of radio inputs.
	* @param {string} [returnDefault='name'] - Value to return if no radio
	*                                         input is selected.
	* @returns {string} The selected value or the fallback value.
	*/
	getSelectedRadio(radios, returnDefault = 'name') {
		return Array.from(radios).find(radio => radio.checked)?.value || returnDefault;
	},
	
	/**
	 * Updates the live region with a contextual status message for search/filter results.
	 *
	 * @since 1.0.0
	 * @param {HTMLElement} feedElement - The element containing result items.
	 * @param {string} query - The search input value.
	 * @param {HTMLSelectElement} deptSelect - The department <select> element.
	 * @param {boolean} isLoading - Whether results are currently loading.
	 * @returns {void}
	 */
	setDirectoryStatusMessage(feedElement, query, deptSelect, isLoading) {
		const statusEl = document.getElementById('directoryStatusMessage');
		if (!statusEl) return;
	
		const selectedDept = deptSelect?.options[deptSelect.selectedIndex]?.value || '';
		const deptLabel = deptSelect?.options[deptSelect.selectedIndex]?.text || '';
		const count = feedElement?.querySelectorAll('.directory-item')?.length || 0;
	
		// No filters applied: clear the message
		if (!query && (!selectedDept || selectedDept === '')) {
			statusEl.textContent = '';
			return;
		}
	
		// If loading, show "Searching..." regardless of count
		if (isLoading) {
			statusEl.textContent = 'Searching the directory...';
			return;
		}
	
		// Otherwise, show result message based on count
		let message = '';
	
		if (count === 0) {
			message = 'No matches found';
			if (query) message += ` for “${query}”`;
			if (deptLabel) message += ` in ${deptLabel}`;
		} else {
			message = `Showing ${count} result${count !== 1 ? 's' : ''}`;
			if (query) message += ` for “${query}”`;
			if (deptLabel) message += ` in ${deptLabel}`;
		}
	
		statusEl.textContent = message;
	}
};

if (document.readyState === 'loading') {
	document.addEventListener('DOMContentLoaded', () => DirectorySearch.init());
} else {
	DirectorySearch.init();
}

window.addEventListener('popstate', () => {
	// Ensure we're on a page with the directory search container
	if (!DirectorySearch.container || !DirectorySearch.deptSelect) return;
	
	const deptQueryVar = UIUtils.getParameterByName('d');
	const currentDept = DirectorySearch.deptSelect.value;
	
	if (deptQueryVar !== currentDept) {
		UIUtils.setSelectValue(DirectorySearch.deptSelect, deptQueryVar);
		DirectorySearch.loadDeptAndResults(deptQueryVar);
	}
});
